import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import Swal from 'sweetalert2';
import {
  AlcanceOpcion,
  BonosService,
  Campana,
  ConfigBonos,
  NuevaCampana,
  OpcionesCampana,
} from 'src/app/shared/services/bonos.service';

/**
 * El formulario de campaña.
 *
 * La idea que lo gobierna: **el formulario no puede ni proponer una
 * combinación imposible.** Las monedas que se ofrecen salen del alcance
 * elegido, y el alcance con sus monedas sale del catálogo del backend — CLP
 * para Venezuela no es un error que se avisa, es una opción que no aparece. Y
 * cuando entre un país nuevo, este formulario ya lo sabe sin que nadie toque
 * una línea.
 *
 * La regla que hay detrás, en una frase: una campaña paga en **USDT siempre**,
 * o en la moneda local de su alcance **si ese alcance tiene una**. Chile solo,
 * en pesos o USDT; Venezuela sola, en bolívares o USDT; los dos países juntos,
 * sólo USDT — porque no hay una moneda local que sirva para los dos.
 *
 * Lo que sí se valida acá es la forma: el código, los decimales de la moneda,
 * que la fecha de fin no sea anterior a la de inicio. La regla la vuelve a
 * validar el backend, y esa es la que manda; esto es para no hacer ir y venir
 * al operador por un punto decimal.
 */
@Component({
  selector: 'app-crear-campana-modal',
  templateUrl: './crear-campana-modal.component.html',
  styleUrls: ['./crear-campana-modal.component.sass'],
})
export class CrearCampanaModalComponent implements OnInit {
  @Output() creada = new EventEmitter<Campana>();
  @Output() cancelar = new EventEmitter<void>();

  cargando = true;
  guardando = false;
  error = '';

  opciones: OpcionesCampana | null = null;
  config: ConfigBonos | null = null;

  // --- el formulario ---
  nombre = '';
  codigo = '';
  alcanceCodigo = '';
  alcancePublico = 'TODOS';
  inactivoDias: number | null = null;
  moneda = '';
  monto = '';
  unidadesTotal: number | null = null;
  vigenciaDesde = '';
  vigenciaHasta = '';
  reservaDias: number | null = null;
  condicionAcreditacion = 'AL_RECLAMAR';
  pausaPct: number | null = null;
  pausaMinutos: number | null = null;

  constructor(private bonos: BonosService) {}

  async ngOnInit(): Promise<void> {
    try {
      const [opciones, config] = await Promise.all([
        this.bonos.opciones(),
        this.bonos.config().catch(() => null),
      ]);
      this.opciones = opciones;
      this.config = config;

      // Los defaults del freno salen del parámetro y no de una constante del
      // front: así ninguna campaña queda sin freno por olvido, y el número que
      // ve el operador es el que el sistema va a usar.
      this.pausaPct = config?.pausaPct ?? null;
      this.pausaMinutos = config?.pausaMinutos ?? null;
      this.inactivoDias = config?.inactivoDias ?? null;

      // Arranca hoy y dura un mes: es lo que casi siempre se quiere, y una
      // fecha vacía en un formulario de plata invita a dejarla vacía.
      const hoy = new Date();
      const enUnMes = new Date(hoy.getTime());
      enUnMes.setMonth(enUnMes.getMonth() + 1);
      this.vigenciaDesde = this.aYmd(hoy);
      this.vigenciaHasta = this.aYmd(enUnMes);
    } catch (e: any) {
      this.error =
        e?.error?.message ||
        'No pudimos leer el catálogo de alcances. Sin eso el formulario no puede ofrecer monedas.';
    } finally {
      this.cargando = false;
    }
  }

  private aYmd(d: Date): string {
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const dia = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mes}-${dia}`;
  }

  // ------------------------------------------------------------- catálogo

  get alcances(): AlcanceOpcion[] {
    return this.opciones?.alcances ?? [];
  }

  /** Las monedas del alcance elegido. Vacío hasta que se elija uno. */
  get monedas(): string[] {
    const a = this.alcances.find((x) => x.alcance === this.alcanceCodigo);
    return a?.monedas ?? [];
  }

  get publicos(): string[] {
    return this.opciones?.publicos ?? [];
  }

  get condiciones(): string[] {
    return this.opciones?.condiciones ?? [];
  }

  /**
   * Al cambiar el alcance se limpia la moneda si dejó de servir.
   *
   * Sin esto quedaría elegido «CLP» con el alcance en Venezuela: el chip no se
   * ve más, pero el valor sigue adentro y el backend lo rechaza al guardar.
   * Un formulario que guarda un valor que ya no muestra es una trampa.
   */
  elegirAlcance(codigo: string): void {
    this.alcanceCodigo = codigo;
    if (this.moneda && !this.monedas.includes(this.moneda)) this.moneda = '';
    // Con una sola moneda posible no hay nada que elegir.
    if (this.monedas.length === 1) this.moneda = this.monedas[0];
  }

  elegirMoneda(m: string): void {
    this.moneda = m;
  }

  elegirPublico(p: string): void {
    this.alcancePublico = p;
    if (p !== 'INACTIVO') this.inactivoDias = null;
    else if (!this.inactivoDias) this.inactivoDias = this.config?.inactivoDias ?? 60;
  }

  publicoTexto(p: string): string {
    if (p === 'SIN_ENVIOS') return 'Nuevos, sin ningún envío';
    if (p === 'INACTIVO') return 'Inactivos, para reactivar';
    return 'Todos los del alcance';
  }

  condicionTexto(c: string): string {
    if (c === 'AL_FONDEAR') return 'Cuando cargue saldo';
    if (c === 'AL_PRIMER_ENVIO') return 'Cuando haga su primer envío';
    return 'Al reclamar el bono';
  }

  alcanceTexto(a: AlcanceOpcion): string {
    return a.nombre || a.alcance;
  }

  // -------------------------------------------------------------- avisos

  /** El código, ya normalizado como lo va a guardar el backend. */
  get codigoNormalizado(): string {
    return this.bonos.normalizarCodigo(this.codigo);
  }

  get errorCodigo(): string | null {
    if (!this.codigo.trim()) return null;
    const r = this.bonos.validarCodigo(this.codigo);
    return r.valido ? null : r.mensaje ?? null;
  }

  get decimales(): number {
    return this.bonos.decimalesDe(this.moneda);
  }

  /**
   * El error del monto, mientras se escribe.
   *
   * Los decimales son los de la moneda: un bono de 10.000,50 pesos lo rechaza
   * el catálogo, y decirlo acá evita mandar el formulario para enterarse.
   */
  get errorMonto(): string | null {
    const m = this.monto.trim();
    if (!m) return null;
    if (!/^\d+(\.\d+)?$/.test(m)) {
      return 'Sólo números, con punto para los decimales.';
    }
    if (Number(m) <= 0) return 'Un bono de 0 no le paga a nadie.';
    const dec = (m.split('.')[1] ?? '').length;
    if (dec > this.decimales) {
      return this.decimales === 2
        ? `${this.moneda} admite 2 decimales.`
        : `${this.moneda} admite hasta ${this.decimales} decimales.`;
    }
    return null;
  }

  /** La bolsa: unidades por monto. Es el número que se autoriza al activar. */
  get bolsa(): string | null {
    if (!this.monto || !this.unidadesTotal || !this.moneda) return null;
    return this.bonos.bolsa(this.monto, this.unidadesTotal, this.moneda);
  }

  /** El monto como lo lee una persona: `20.000` y no `20000.00000000`. */
  legible(monto: string | number | null | undefined): string {
    return this.bonos.montoLegible(monto);
  }

  /** `true` si la moneda elegida todavía no tiene camino para pagar. */
  get monedaSinCamino(): boolean {
    return this.bonos.sinCamino(this.moneda, this.opciones);
  }

  get errorFechas(): string | null {
    if (!this.vigenciaDesde || !this.vigenciaHasta) return null;
    if (this.vigenciaHasta < this.vigenciaDesde) {
      return 'La fecha de fin no puede ser anterior a la de inicio.';
    }
    return null;
  }

  /** Arranca en el futuro: queda PROGRAMADA y se activa sola ese día. */
  get arrancaDespues(): boolean {
    if (!this.vigenciaDesde) return false;
    return this.vigenciaDesde > this.aYmd(new Date());
  }

  /** `true` si esta bolsa va a necesitar un segundo operador para activarse. */
  get necesitaSegundaFirma(): boolean {
    if (!this.config || !this.bolsa || !this.moneda) return false;
    // El umbral de la moneda elegida, no uno global: comparar una bolsa en
    // dólares contra un umbral en pesos no quiere decir nada.
    const umbral = this.config.dobleControl?.[this.moneda] ?? 0;
    return Number(this.bolsa) >= umbral;
  }

  get puedeGuardar(): boolean {
    return Boolean(
      this.nombre.trim() &&
        this.codigoNormalizado &&
        !this.errorCodigo &&
        this.alcanceCodigo &&
        this.moneda &&
        this.monto.trim() &&
        !this.errorMonto &&
        this.unidadesTotal &&
        this.unidadesTotal >= 1 &&
        this.vigenciaDesde &&
        this.vigenciaHasta &&
        !this.errorFechas &&
        (this.alcancePublico !== 'INACTIVO' || (this.inactivoDias ?? 0) >= 1) &&
        !this.guardando
    );
  }

  // -------------------------------------------------------------- guardar

  async guardar(): Promise<void> {
    if (!this.puedeGuardar) return;

    this.guardando = true;
    try {
      const cuerpo: NuevaCampana = {
        nombre: this.nombre.trim(),
        codigo: this.codigoNormalizado,
        alcanceCodigo: this.alcanceCodigo,
        alcancePublico: this.alcancePublico,
        moneda: this.moneda,
        monto: this.monto.trim(),
        unidadesTotal: Number(this.unidadesTotal),
        // Del día completo: `desde` arranca a las 00:00 y `hasta` termina a las
        // 23:59:59. Sin la hora, una campaña «hasta el 30» moriría a la
        // medianoche del 29 al 30 y perdería el último día entero.
        vigenciaDesde: `${this.vigenciaDesde}T00:00:00`,
        vigenciaHasta: `${this.vigenciaHasta}T23:59:59`,
        condicionAcreditacion: this.condicionAcreditacion,
      };

      if (this.alcancePublico === 'INACTIVO' && this.inactivoDias) {
        cuerpo.inactivoDias = Number(this.inactivoDias);
      }
      if (this.reservaDias) cuerpo.reservaDias = Number(this.reservaDias);
      if (this.pausaPct) cuerpo.pausaPct = Number(this.pausaPct);
      if (this.pausaMinutos) cuerpo.pausaMinutos = Number(this.pausaMinutos);

      const campana = await this.bonos.crearCampana(cuerpo);
      this.creada.emit(campana);
    } catch (e: any) {
      const msg = e?.error?.message;
      const texto = Array.isArray(msg) ? msg.join(' ') : msg;

      // El código repetido es el error probable y tiene su propio texto: los
      // códigos no se reciclan a propósito, así que no es un dato a corregir
      // sino uno a cambiar.
      if (e?.status === 409) {
        Swal.fire({
          title: 'Ese código ya se usó',
          text:
            texto ||
            'Los códigos no se reciclan: un cliente con la pieza vieja reclamaría la campaña nueva.',
          icon: 'warning',
          confirmButtonColor: '#2F4A2B',
        });
        return;
      }

      Swal.fire({
        title: 'No se pudo crear',
        text: texto || 'Revisá los valores.',
        icon: 'error',
      });
    } finally {
      this.guardando = false;
    }
  }

  cerrar(): void {
    this.cancelar.emit();
  }
}
